<template>
  <div class="intervention-plan">
    <div class="page-header">
      <h1>Intervention Plans</h1>

      <div class="student-selector">
        <div class="search-wrapper">
          <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="6"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="studentSearch"
            placeholder="Search students..."
            class="search-input"
          />
          <button v-if="selectedChildID" class="clear-btn" @mousedown.prevent="clearStudent">✕</button>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="student-filters" v-if="students.length && !selectedChildID">
      <select v-model="filterAge" class="filter-select">
        <option value="">All Ages</option>
        <option value="0-5">Under 6</option>
        <option value="6-10">6–10</option>
        <option value="11-15">11–15</option>
        <option value="16+">16+</option>
      </select>
      <select v-model="filterArea" class="filter-select">
        <option value="">All Areas</option>
        <option value="screenings">Screening</option>
        <option value="assessments">Assessment</option>
        <option value="interventions">Intervention</option>
      </select>
      <select v-model="filterStatus" class="filter-select">
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>

    <!-- Student list -->
    <div class="student-list-panel" v-if="students.length && !selectedChildID">
      <ul class="student-list">
        <li
          v-for="s in filteredStudents"
          :key="s.child_id"
          @click="selectStudent(s)"
          :class="{ active: s.child_id === selectedChildID }"
        >
          {{ s.first_name }} {{ s.last_name }}
        </li>
        <li v-if="students.length && !filteredStudents.length" class="no-students">No students match your search</li>
      </ul>
    </div>

    <div v-if="!selectedChildID" class="empty-state">
      Select a student to view or create intervention plans.
    </div>

    <div v-else class="content">
      <button class="btn-back" @click="clearStudent">← Back </button>

      <div class="content-header">
        <h2>{{ selectedStudent ? selectedStudent.first_name + ' ' + selectedStudent.last_name : '' }}</h2>
        <div class="content-actions">
          <button class="btn-ghost" @click="printPlan">🖨 Print</button>
          <button class="btn-danger-sm" @click="deleteAllPlans" v-if="plans.length">Delete All</button>
          <button class="btn-secondary" @click="pullFromIEP" :disabled="!iepGoals.length || pullingFromIEP">
            Pull from IEP ({{ iepGoals.length }} goals)
          </button>
          <button class="btn-primary" @click="addBlankPlan">+ Add Goal</button>
        </div>
      </div>

      <!-- Site info -->
      <div class="site-info" v-if="selectedStudent && (selectedStudent.site_name || selectedStudent.location_in_site)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span v-if="selectedStudent.site_name">{{ selectedStudent.site_name }}</span>
        <span v-if="selectedStudent.site_name && selectedStudent.location_in_site" class="site-sep">·</span>
        <span v-if="selectedStudent.location_in_site">{{ selectedStudent.location_in_site }}</span>
      </div>

      <!-- Progress summary -->
      <div class="progress-summary" v-if="plans.length">
        <div class="summary-stat">
          <span class="summary-num">{{ plans.length }}</span>
          <span class="summary-label">Total Goals</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-stat">
          <span class="summary-num status-color-progress">{{ plans.filter(p => p.status === 'In Progress').length }}</span>
          <span class="summary-label">In Progress</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-stat">
          <span class="summary-num status-color-met">{{ plans.filter(p => p.status === 'Met').length }}</span>
          <span class="summary-label">Met</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-stat">
          <span class="summary-num status-color-not-started">{{ plans.filter(p => p.status === 'Not Started').length }}</span>
          <span class="summary-label">Not Started</span>
        </div>
        <div class="summary-bar">
          <div class="bar-segment bar-met"          :style="{ width: pct('Met') }"></div>
          <div class="bar-segment bar-in-progress"  :style="{ width: pct('In Progress') }"></div>
          <div class="bar-segment bar-not-started"  :style="{ width: pct('Not Started') }"></div>
        </div>
      </div>

      <!-- Status filter -->
      <div class="filter-row" v-if="plans.length">
        <button
          v-for="f in ['All','Not Started','In Progress','Met']"
          :key="f"
          class="filter-btn"
          :class="{ active: statusFilter === f }"
          @click="statusFilter = f"
        >{{ f }}</button>
        <button class="filter-btn stats-individual-btn" @click="$router.push(`/specialistStatisticsIndividual/${selectedChildID}`)">Statistics - Individual</button>
      </div>

      <div v-if="plans.length === 0" class="empty-state">
        No intervention plans yet. Pull goals from IEP or add one manually.
      </div>

      <div v-for="plan in filteredPlans" :key="plan.plan_id" class="plan-card">
        <div class="plan-top">
          <div class="plan-name-row">
            <input
              class="plan-name-input"
              v-model="plan.name"
              placeholder="Goal name..."
            />
            <select class="status-select" v-model="plan.status" :class="statusClass(plan.status)">
              <option>Not Started</option>
              <option>In Progress</option>
              <option>Met</option>
            </select>
            <button class="btn-danger-sm" @click="deletePlan(plan.plan_id)">Delete</button>
          </div>
          <div class="plan-goal-row">
            <textarea
              class="goal-input"
              v-model="plan.goal"
              placeholder="Describe the goal..."
              @input="autoResize($event.target)"
            />
          </div>

          <div class="plan-fields">
            <div class="field">
              <label>Strategy</label>
              <textarea v-model="plan.strategy" rows="2" placeholder="Intervention strategy..." @input="autoResize($event.target)" />
            </div>
            <div class="field-row">
              <div class="field">
                <label>Frequency</label>
                <input v-model="plan.frequency" placeholder="e.g. 3x per week" />
              </div>
              <div class="field">
                <label>Duration</label>
                <input v-model="plan.duration" placeholder="e.g. 30 minutes" />
              </div>
              <div class="field">
                <label>Responsible Person</label>
                <input v-model="plan.responsible_person" placeholder="e.g. Dr. Smith" />
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <label>Estimated Completion</label>
                <input type="date" v-model="plan.due_date" />
              </div>
              <div class="field">
                <label>Actual Completion</label>
                <input type="date" v-model="plan.actual_completion_date" />
              </div>
              <div class="field delta-field" v-if="plan.due_date">
                <label>Delta</label>
                <span :class="deltaClass(plan.due_date, plan.actual_completion_date)">
                  {{ deltaLabel(plan.due_date, plan.actual_completion_date) }}
                </span>
                <select v-model="plan.delay_reason" class="delay-reason-select">
                  <option value="">Select a reason...</option>
                  <option v-for="reason in delayReasons" :key="reason" :value="reason">{{ reason }}</option>
                </select>
                <label class="additional-info-label">Additional info:</label>
                <textarea v-model="plan.additional_info" class="additional-info-input" rows="2" placeholder="Add any additional information..."></textarea>
              </div>
            </div>
          </div>

          <div class="plan-actions">
            <button class="btn-primary" @click="savePlan(plan)">Save</button>
          </div>
        </div>

        <!-- Sub-goals -->
        <div class="subgoal-section">
          <div class="session-header">
            <h4>Sub-goals</h4>
            <button class="btn-secondary-sm" @click="toggleAddSubgoal(plan)" :disabled="plan._isNew">+ Add Sub-goal</button>
          </div>

          <div v-if="plan._addingSubgoal" class="subgoal-form">
            <input v-model="plan._newSubgoal.name" placeholder="Sub-goal name..." class="subgoal-name-input" />
            <div class="subgoal-form-row">
              <div class="field">
                <label>Responsible Person</label>
                <input v-model="plan._newSubgoal.responsible_person" placeholder="e.g. Dr. Smith" />
              </div>
              <div class="field">
                <label>Status</label>
                <select v-model="plan._newSubgoal.status">
                  <option>Not Started</option>
                  <option>In Progress</option>
                  <option>Met</option>
                </select>
              </div>
              <div class="field">
                <label>Estimated Completion</label>
                <input type="date" v-model="plan._newSubgoal.due_date" />
              </div>
              <div class="field">
                <label>Actual Completion</label>
                <input type="date" v-model="plan._newSubgoal.actual_completion_date" />
              </div>
            </div>
            <div class="session-form-actions">
              <button class="btn-primary" @click="saveSubgoal(plan)">Save</button>
              <button class="btn-ghost" @click="plan._addingSubgoal = false">Cancel</button>
            </div>
          </div>

          <div v-if="plan.subgoals && plan.subgoals.length > 0" class="subgoal-list">
            <div v-for="sg in plan.subgoals" :key="sg.subgoal_id" class="subgoal-row">
              <div class="subgoal-main">
                <input class="subgoal-name-edit" v-model="sg.name" placeholder="Sub-goal name..." />
                <select class="status-select-sm" v-model="sg.status" :class="statusClass(sg.status)">
                  <option>Not Started</option>
                  <option>In Progress</option>
                  <option>Met</option>
                </select>
              </div>
              <div class="subgoal-meta">
                <div class="field">
                  <label>Responsible</label>
                  <input v-model="sg.responsible_person" placeholder="e.g. Dr. Smith" />
                </div>
                <div class="field">
                  <label>Estimated</label>
                  <input type="date" v-model="sg.due_date" />
                </div>
                <div class="field">
                  <label>Actual</label>
                  <input type="date" v-model="sg.actual_completion_date" />
                </div>
                <div class="field" v-if="sg.due_date">
                  <label>Delta</label>
                  <span :class="deltaClass(sg.due_date, sg.actual_completion_date)">
                    {{ deltaLabel(sg.due_date, sg.actual_completion_date) }}
                  </span>
                  <select v-model="sg.delay_reason" class="delay-reason-select">
                    <option value="">Select a reason...</option>
                    <option v-for="reason in delayReasons" :key="reason" :value="reason">{{ reason }}</option>
                  </select>
                  <label class="additional-info-label">Additional info:</label>
                  <textarea v-model="sg.additional_info" class="additional-info-input" rows="2" placeholder="Add any additional information..."></textarea>
                </div>
              </div>
              <div class="subgoal-actions">
                <button class="btn-secondary-sm" @click="updateSubgoal(sg)">Save</button>
                <button class="btn-danger-sm" @click="deleteSubgoal(plan, sg.subgoal_id)">Delete</button>
              </div>
            </div>
          </div>
          <p v-else-if="!plan._addingSubgoal" class="no-sessions">No sub-goals yet.</p>
        </div>

        <!-- Session Log -->
        <div class="session-section">
          <div class="session-header">
            <h4>Session Log</h4>
            <button class="btn-secondary-sm" @click="toggleAddSession(plan)">+ Add Session</button>
          </div>

          <div v-if="plan._addingSession" class="session-form">
            <input type="date" v-model="plan._newSession.session_date" />
            <input v-model="plan._newSession.notes" placeholder="Session notes..." />
            <input v-model="plan._newSession.progress" placeholder="Progress update..." />
            <div class="session-form-actions">
              <button class="btn-primary" @click="saveSession(plan)">Save</button>
              <button class="btn-ghost" @click="plan._addingSession = false">Cancel</button>
            </div>
          </div>

          <div v-if="plan.sessions && plan.sessions.length > 0" class="session-table-wrapper">
            <table class="session-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Notes</th>
                  <th>Progress</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="session in plan.sessions" :key="session.session_id">
                  <td class="date-cell">{{ session.session_date }}</td>
                  <td>{{ session.notes }}</td>
                  <td>{{ session.progress }}</td>
                  <td>
                    <button class="btn-danger-sm" @click="deleteSession(plan, session.session_id)">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="no-sessions">No sessions logged yet.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InterventionPlan',
  data() {
    return {
      specialistUserID: null,
      students: [],
      selectedChildID: '',
      studentSearch: '',
      filterAge: '',
      filterArea: '',
      filterStatus: '',
      plans: [],
      iepGoals: [],
      statusFilter: 'All',
      pullingFromIEP: false,
      delayReasons: [
        "The student/patient did not participate/try enough",
        "The team overestimated the student's ability to progress",
        "The student was sick/absent",
        "The team underestimated the student's ability to progress",
        "I was overloaded with other tasks, so couldn't make time to work with the student/patient enough",
      ],
    };
  },
  computed: {
    selectedStudent() {
      return this.students.find(s => s.child_id === this.selectedChildID) || null;
    },
    filteredStudents() {
      const q = this.studentSearch.toLowerCase().trim();
      return this.students.filter(s => {
        if (q && !`${s.first_name} ${s.last_name}`.toLowerCase().includes(q)) return false;

        if (this.filterAge) {
          const age = this.calcAge(s.date_of_birth);
          if (age === null) return false;
          if (this.filterAge === '0-5' && age > 5) return false;
          if (this.filterAge === '6-10' && (age < 6 || age > 10)) return false;
          if (this.filterAge === '11-15' && (age < 11 || age > 15)) return false;
          if (this.filterAge === '16+' && age < 16) return false;
        }

        if (this.filterArea) {
          const bucket = s[this.filterArea];
          if (!bucket || (bucket.total === 0 && (!bucket.remaining || bucket.remaining.length === 0))) return false;
        }

        if (this.filterStatus) {
          if (this.filterStatus === 'active' && !s.activity_status) return false;
          if (this.filterStatus === 'inactive' && s.activity_status) return false;
        }

        return true;
      });
    },
    filteredPlans() {
      if (this.statusFilter === 'All') return this.plans;
      return this.plans.filter(p => p.status === this.statusFilter);
    },
  },
  async mounted() {
    await this.init();
    const returnChildID = this.$route.query.childID;
    if (returnChildID) {
      const student = this.students.find(s => s.child_id === returnChildID);
      if (student) this.selectStudent(student);
    }
  },
  watch: {
    plans() {
      this.$nextTick(() => this.resizeAll());
    },
  },
  methods: {
    baseUrl() {
      return process.env.VUE_APP_REVERSE_PROXY_ENDPOINT || 'http://localhost:8081';
    },
    calcAge(dob) {
      if (!dob) return null;
      const birth = new Date(dob);
      if (isNaN(birth)) return null;
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    },
    async init() {
      const base = this.baseUrl();
      try {
        const authResp = await fetch(`${base}/api/auth/get-access-cookie-data`, { credentials: 'include' });
        if (!authResp.ok) return;
        const authData = await authResp.json();
        this.specialistUserID = authData.id;

        const studentsResp = await fetch(`${base}/api/specialist/user/${this.specialistUserID}/searchFilter`, { credentials: 'include' });
        if (studentsResp.ok) {
          this.students = await studentsResp.json() || [];
        }
      } catch (err) {
        console.error('Init error:', err);
      }
    },
    autoResize(el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    resizeAll() {
      this.$nextTick(() => {
        this.$el.querySelectorAll('textarea').forEach(el => this.autoResize(el));
      });
    },
    selectStudent(student) {
      this.selectedChildID = student.child_id;
      this.studentSearch = `${student.first_name} ${student.last_name}`;
      this.showDropdown = false;
      this.onStudentChange();
    },
    clearStudent() {
      this.selectedChildID = '';
      this.studentSearch = '';
      this.plans = [];
      this.iepGoals = [];
    },
    async onStudentChange() {
      this.plans = [];
      this.iepGoals = [];
      if (!this.selectedChildID) return;
      await Promise.all([this.fetchPlans(), this.fetchIEPGoals()]);
    },
    async fetchPlans() {
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/child/${this.selectedChildID}`, { credentials: 'include' });
        if (!resp.ok) return;
        const data = await resp.json() || [];
        this.plans = data.map(p => ({
          ...p,
          delay_reason: p.delay_reason || '',
          additional_info: p.additional_info || '',
          _addingSession: false,
          _newSession: { session_date: '', notes: '', progress: '' },
          _addingSubgoal: false,
          _newSubgoal: { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started', delay_reason: '', additional_info: '' },
          subgoals: (p.subgoals || []).map(sg => ({ ...sg, delay_reason: sg.delay_reason || '', additional_info: sg.additional_info || '' })),
        }));
        this.resizeAll();
      } catch (err) {
        console.error('fetchPlans error:', err);
      }
    },
    async fetchIEPGoals() {
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/iep/${this.selectedChildID}`, { credentials: 'include' });
        if (!resp.ok) return;
        const ieps = await resp.json() || [];
        this.iepGoals = ieps.flatMap(iep => iep.goals || []);
      } catch (err) {
        console.error('fetchIEPGoals error:', err);
      }
    },
    async pullFromIEP() {
      if (this.pullingFromIEP) return;
      this.pullingFromIEP = true;
      try {
        const existingGoals = this.plans.map(p => p.goal.trim().toLowerCase());
        const newGoals = this.iepGoals.filter(g => !existingGoals.includes(g.trim().toLowerCase()));
        for (const goal of newGoals) {
          const saved = await this.createPlan({ name: '', goal, strategy: '', frequency: '', duration: '', due_date: this.parseGoalDate(goal), actual_completion_date: '', responsible_person: '' });
          if (saved) this.plans.push({ ...saved, _addingSession: false, _newSession: { session_date: '', notes: '', progress: '' }, _addingSubgoal: false, _newSubgoal: { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started' } });
        }
      } finally {
        this.pullingFromIEP = false;
      }
    },
    addBlankPlan() {
      const tempPlan = {
        plan_id: '__new__' + Date.now(),
        goal: '',
        strategy: '',
        frequency: '',
        duration: '',
        name: '',
        due_date: '',
        actual_completion_date: '',
        responsible_person: '',
        status: 'Not Started',
        sessions: [],
        subgoals: [],
        _addingSession: false,
        _newSession: { session_date: '', notes: '', progress: '' },
        _addingSubgoal: false,
        _newSubgoal: { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started', delay_reason: '' },
        _isNew: true,
      };
      this.plans.push(tempPlan);
    },
    async savePlan(plan) {
      if (plan._isNew) {
        const saved = await this.createPlan({ name: plan.name || '', goal: plan.goal, strategy: plan.strategy, frequency: plan.frequency, duration: plan.duration, due_date: plan.due_date || '', actual_completion_date: plan.actual_completion_date || '', responsible_person: plan.responsible_person || '', delay_reason: plan.delay_reason || '', additional_info: plan.additional_info || '' });
        if (saved) {
          const idx = this.plans.findIndex(p => p.plan_id === plan.plan_id);
          if (idx !== -1) this.plans.splice(idx, 1, { ...saved, _addingSession: false, _newSession: { session_date: '', notes: '', progress: '' }, _addingSubgoal: false, _newSubgoal: { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started' } });
        }
        return;
      }
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/plan/${plan.plan_id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: plan.name || '', goal: plan.goal, strategy: plan.strategy, frequency: plan.frequency, duration: plan.duration, status: plan.status, due_date: plan.due_date || '', actual_completion_date: plan.actual_completion_date || '', responsible_person: plan.responsible_person || '', delay_reason: plan.delay_reason || '', additional_info: plan.additional_info || '' }),
        });
        if (!resp.ok) console.error('Save plan failed:', resp.status);
      } catch (err) {
        console.error('savePlan error:', err);
      }
    },
    async createPlan(params) {
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/child/${this.selectedChildID}/specialist/${this.specialistUserID}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });
        if (!resp.ok) return null;
        return await resp.json();
      } catch (err) {
        console.error('createPlan error:', err);
        return null;
      }
    },
    async deleteAllPlans() {
      if (!confirm(`Delete all ${this.plans.length} plans for this student? This cannot be undone.`)) return;
      const base = this.baseUrl();
      for (const plan of [...this.plans]) {
        try {
          await fetch(`${base}/api/intervention/plan/${plan.plan_id}`, { method: 'DELETE', credentials: 'include' });
        } catch (err) {
          console.error('deleteAllPlans error:', err);
        }
      }
      this.plans = [];
    },
    async deletePlan(planID) {
      const base = this.baseUrl();
      try {
        await fetch(`${base}/api/intervention/plan/${planID}`, { method: 'DELETE', credentials: 'include' });
        this.plans = this.plans.filter(p => p.plan_id !== planID);
      } catch (err) {
        console.error('deletePlan error:', err);
      }
    },
    toggleAddSession(plan) {
      plan._addingSession = !plan._addingSession;
      if (plan._addingSession) plan._newSession = { session_date: '', notes: '', progress: '' };
    },
    async saveSession(plan) {
      if (plan._isNew) return;
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/plan/${plan.plan_id}/session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(plan._newSession),
        });
        if (!resp.ok) return;
        const session = await resp.json();
        plan.sessions.unshift(session);
        plan._addingSession = false;
        plan._newSession = { session_date: '', notes: '', progress: '' };
      } catch (err) {
        console.error('saveSession error:', err);
      }
    },
    async deleteSession(plan, sessionID) {
      const base = this.baseUrl();
      try {
        await fetch(`${base}/api/intervention/session/${sessionID}`, { method: 'DELETE', credentials: 'include' });
        plan.sessions = plan.sessions.filter(s => s.session_id !== sessionID);
      } catch (err) {
        console.error('deleteSession error:', err);
      }
    },
    parseGoalDate(goalText) {
      if (!goalText) return '';
      const months = { january:1,february:2,march:3,april:4,may:5,june:6,july:7,august:8,september:9,october:10,november:11,december:12,jan:1,feb:2,mar:3,apr:4,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
      // Trim any leading noise to find "By ..." anywhere near the start
      const text = goalText.replace(/^[^B]*(By\s)/i, 'By ').trim();
      // "By 5/10/2018" or "By 5/10, 2017" (comma or space between day and year)
      const mdyMatch = text.match(/^by\s+(\d{1,2})[/](\d{1,2})[/,\s]+(\d{4})/i);
      if (mdyMatch) {
        const [, m, d, y] = mdyMatch;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      // "By May 10, 2018" or "By May 10,2018" (space optional after comma)
      const monthDayYear = text.match(/^by\s+([a-z]+)\s+(\d{1,2})\s*,?\s*(\d{4})/i);
      if (monthDayYear) {
        const [, mon, d, y] = monthDayYear;
        const m = months[mon.toLowerCase()];
        if (m) return `${y}-${String(m).padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
      // "By May 2018" (no day)
      const monthYear = text.match(/^by\s+([a-z]+)\s+(\d{4})/i);
      if (monthYear) {
        const [, mon, y] = monthYear;
        const m = months[mon.toLowerCase()];
        if (m) return `${y}-${String(m).padStart(2, '0')}-01`;
      }
      return '';
    },
    toggleAddSubgoal(plan) {
      plan._addingSubgoal = !plan._addingSubgoal;
      if (plan._addingSubgoal) plan._newSubgoal = { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started' };
    },
    async saveSubgoal(plan) {
      if (plan._isNew) return;
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/plan/${plan.plan_id}/subgoal`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(plan._newSubgoal),
        });
        if (!resp.ok) return;
        const sg = await resp.json();
        plan.subgoals.push(sg);
        plan._addingSubgoal = false;
        plan._newSubgoal = { name: '', due_date: '', actual_completion_date: '', responsible_person: '', status: 'Not Started' };
      } catch (err) {
        console.error('saveSubgoal error:', err);
      }
    },
    async updateSubgoal(sg) {
      const base = this.baseUrl();
      try {
        const resp = await fetch(`${base}/api/intervention/subgoal/${sg.subgoal_id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: sg.name, due_date: sg.due_date || '', actual_completion_date: sg.actual_completion_date || '', responsible_person: sg.responsible_person || '', status: sg.status, delay_reason: sg.delay_reason || '', additional_info: sg.additional_info || '' }),
        });
        if (!resp.ok) console.error('updateSubgoal failed:', resp.status);
      } catch (err) {
        console.error('updateSubgoal error:', err);
      }
    },
    async deleteSubgoal(plan, subgoalID) {
      const base = this.baseUrl();
      try {
        await fetch(`${base}/api/intervention/subgoal/${subgoalID}`, { method: 'DELETE', credentials: 'include' });
        plan.subgoals = plan.subgoals.filter(sg => sg.subgoal_id !== subgoalID);
      } catch (err) {
        console.error('deleteSubgoal error:', err);
      }
    },
    monthsDays(from, to) {
      let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
      if (to.getDate() < from.getDate()) months--;
      if (months < 0) months = 0;
      const afterMonths = new Date(from);
      afterMonths.setMonth(afterMonths.getMonth() + months);
      const days = Math.round((to - afterMonths) / 86400000);
      const parts = [];
      if (months > 0) parts.push(`${months}mo`);
      if (days > 0 || months === 0) parts.push(`${days}d`);
      return parts.join(' ');
    },
    deltaLabel(estimated, actual) {
      if (!estimated) return '';
      if (!actual) return 'N/A';
      const est = new Date(estimated);
      const act = new Date(actual);
      const totalDays = Math.round((act - est) / 86400000);
      if (totalDays === 0) return 'Completed on time';
      if (totalDays > 0) return `${this.monthsDays(est, act)} late`;
      return `${this.monthsDays(act, est)} early`;
    },
    deltaClass(estimated, actual) {
      if (!estimated || !actual) return 'delta-na';
      const days = Math.round((new Date(actual) - new Date(estimated)) / 86400000);
      if (days === 0) return 'delta-ontime';
      return days > 0 ? 'delta-late' : 'delta-early';
    },
    statusClass(status) {
      if (status === 'Met') return 'status-met';
      if (status === 'In Progress') return 'status-in-progress';
      return 'status-not-started';
    },
    pct(status) {
      if (!this.plans.length) return '0%';
      const count = this.plans.filter(p => p.status === status).length;
      return (count / this.plans.length * 100).toFixed(1) + '%';
    },
    printPlan() {
      window.print();
    },
  },
};
</script>

<style scoped lang="scss">
.intervention-plan {
  padding: 100px 40px 40px;
  max-width: 1100px;
  margin: 0 auto;
  color: #1F1F1F;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  h1 { font-size: 24px; font-weight: 700; margin: 0; color: #1F1F1F; }
}
.student-selector {
  position: relative;
  min-width: 260px;
}
.search-wrapper {
  display: flex;
  align-items: center;
  background: #fff8ee;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  padding: 0 10px;
  gap: 6px;
}
.search-icon {
  width: 16px;
  height: 16px;
  color: #888;
  flex-shrink: 0;
}
.search-input {
  flex: 1;
  border: none;
  background: none;
  padding: 9px 0;
  font-size: 14px;
  outline: none;
  color: #453425
}
.clear-btn {
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  &:hover { color: #555; }
}
.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 100;
  max-height: 220px;
  overflow-y: auto;
  li {
    padding: 9px 14px;
    font-size: 14px;
    cursor: pointer;
    &:hover { background: #fff8ee; color: #aa6d27; }
  }
 

}
.no-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #888;
  margin: 0;
  z-index: 100;
}
.student-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.filter-select {
  padding: 7px 12px;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  background: #fff8ee;
  font-size: 13px;
  color: #5c300b;
  cursor: pointer;
  &:focus { outline: none; border-color: #aa6d27; }
}
.btn-back {
  background: none;
  border: none;
  color: #aa6d27;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-bottom: 16px;
  display: block;
  &:hover { text-decoration: underline; }
}
.student-list-panel {
  margin-bottom: 24px;
  border: 1px solid #f0d9a8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.student-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 220px;
  overflow-y: auto;
  li {
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
    color: #1F1F1F;
    border-bottom: 1px solid #fff8ee;
    &:last-child { border-bottom: none; }
    &:hover { background: #fff8ee; color: #aa6d27; }
    &.active { background: #f0d9a8; color: #5c300b; font-weight: 600; }
  }
}
.no-students {
  color: #aaa;
  font-size: 13px;
  cursor: default !important;
  &:hover { background: none !important; color: #aaa !important; }
}
.empty-state {
  color: #888;
  font-size: 15px;
  padding: 60px 0;
  text-align: center;
}
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  h2 { font-size: 20px; font-weight: 700; margin: 0; color:#5c2b02; }
}
.content-actions {
  display: flex;
  gap: 10px;
}
.plan-card {
  background: #fff;
  border: 1px solid #f0d9a8;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(231,166,58,0.06);
}
.plan-name-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
}
.plan-name-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  color: #111;
  &::placeholder { color: #bbb; font-weight: 400; }
}
.plan-goal-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 16px;
}
.goal-input {
  flex: 1;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 400;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  min-height: 42px;
  color: #5c300b;
}
.status-select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &.status-met { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
  &.status-in-progress { background: #FFF5E3; color: #9D5f0E; border-color: #fcd34d; }
  &.status-not-started { background: #f3f4f6; color: #6b7280; }
}
.plan-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  
}
.field-row {
  display: flex;
  gap: 16px;
  .field { flex: 1; }
}
.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  label { font-size: 12px; font-weight: 600; color: #aa6d27; text-transform: uppercase; letter-spacing: 0.5px; }
  input, textarea {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    overflow: hidden;
    color: #503926;
  }
}
.plan-actions { display: flex; justify-content: flex-end; }
.session-section {
  border-top: 1px solid #f0d9a8;
  padding-top: 16px;
  margin-top: 16px;
}
.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  h4 { font-size: 14px; font-weight: 600; margin: 0; color: #aa6d27; }
}
.session-form {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  background: #fff8ee;
  padding: 12px;
  border-radius: 8px;
  input {
    padding: 7px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    flex: 1;
    min-width: 140px;
  }
}
.session-form-actions { display: flex; gap: 8px; width: 100%; }
.session-table-wrapper { overflow-x: auto; }
.session-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th {
    text-align: left;
    padding: 8px 10px;
    color: #aa6d27;
    font-weight: 600;
    border-bottom: 1px solid #f0d9a8;
  }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #fff8ee;
    vertical-align: top;
  }
  .date-cell { white-space: nowrap; color: #666; }
}
.no-sessions { font-size: 13px; color: #bbb; margin: 8px 0 0; }

/* Site info */
.site-info {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  margin-bottom: 20px;
  svg { color: #E7A63A; flex-shrink: 0; }
}
.site-sep { color: #ccc; }

/* Delta */
.delta-field { justify-content: flex-end; }
.delta-late  { font-size: 13px; font-weight: 600; color: #c0392b; }
.delta-early { font-size: 13px; font-weight: 600; color: #16a34a; }
.delta-ontime { font-size: 13px; font-weight: 600; color: #2563eb; }
.delta-na { font-size: 13px; font-weight: 600; color: #9ca3af; }
.delay-reason-select { margin-top: 6px; width: 100%; font-size: 12px; padding: 4px 6px; border: 1px solid #ddd; border-radius: 4px; color: #555; }
.additional-info-label { display: block; margin-top: 8px; font-size: 12px; font-weight: 600; color: #aa6d27; }
.additional-info-input { margin-top: 4px; width: 100%; font-size: 12px; padding: 4px 6px; border: 1px solid #f0d9a8; border-radius: 4px; color: #5c300b; resize: vertical; }
.stats-individual-btn { margin-left: 8px; }

/* Subgoals */
.subgoal-section {
  border-top: 1px solid #f0d9a8;
  padding-top: 16px;
  margin-top: 16px;
}
.subgoal-form {
  background: #fff8ee;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.subgoal-name-input {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}
.subgoal-form-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  .field { flex: 1; min-width: 140px; }
  select {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    width: 100%;
  }
}
.subgoal-list { display: flex; flex-direction: column; gap: 10px; }
.subgoal-row {
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fffdf9;
}
.subgoal-main {
  display: flex;
  gap: 10px;
  align-items: center;
}
.subgoal-name-edit {
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
}
.status-select-sm {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &.status-met { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
  &.status-in-progress { background: #FFF5E3; color: #9D5f0E; border-color: #fcd34d; }
  &.status-not-started { background: #f3f4f6; color: #6b7280; }
}
.subgoal-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  .field { flex: 1; min-width: 120px; }
}
.subgoal-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* Progress summary */
.progress-summary {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #fff8ee;
  border: 1px solid #f0d9a8;
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.summary-num {
  font-size: 22px;
  font-weight: 700;
  color: #111;
}
.summary-label {
  font-size: 11px;
  color: #aa6d27;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.summary-divider {
  width: 1px;
  height: 36px;
  background: #f0d9a8;
}
.summary-bar {
  flex: 1;
  min-width: 120px;
  height: 10px;
  border-radius: 5px;
  overflow: hidden;
  background: #f0d9a8;
  display: flex;
}
.bar-segment {
  height: 100%;
  transition: width 0.4s ease;
}
.bar-met         { background: #34d399; }
.bar-in-progress { background: #fbbf24; }
.bar-not-started { background: #d1d5db; }
.status-color-progress  { color: #aa6d27; }
.status-color-met       { color: #065f46; }
.status-color-not-started { color: #6b7280; }

/* Status filter */
.filter-row {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.filter-btn {
  padding: 6px 16px;
  border: 1px solid #f0d9a8;
  border-radius: 20px;
  background: #fff8ee;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover { border-color: #aa6d27; color: #aa6d27; }
  &.active { background: #aa6d27; color: #fff; border-color: #aa6d27; }
}

/* Print */
@media print {
  .page-header .student-selector,
  .content-actions,
  .filter-row,
  .plan-actions,
  .session-header button,
  .session-form,
  .btn-danger-sm { display: none !important; }
  .intervention-plan { padding: 20px; max-width: 100%; }
  .plan-card { box-shadow: none; border: 1px solid #ccc; break-inside: avoid; }
}

/* Buttons */
.btn-primary {
  background: #aa6d27;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #E7A63A; }
  &:disabled { opacity: 0.5; cursor: default; }
}
.btn-secondary {
  background: #fff8ee;
  color: #aa6d27;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: default; }
}
.btn-secondary-sm {
  background: #fff8ee;
  color: #aa6d27;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.btn-danger-sm {
  background: none;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  color: #c0392b;
  cursor: pointer;
  &:hover { background: #fff0f0; }
}
.btn-ghost {
  background: none;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 13px;
  cursor: pointer;
}
</style>
